import * as THREE from 'three';
import type { CameraMode, CameraModeId } from './CameraMode';

/**
 * Engine no longer permanently owns one camera; it owns this manager
 * instead. Concrete camera modes (behind-ball, flyover, etc.) are plugged
 * in from outside — Engine/CameraManager only ever depend on the
 * CameraMode interface, never a concrete implementation, so adding the
 * 6th camera mode next year touches zero engine code.
 */
export class CameraManager {
  private readonly modes = new Map<CameraModeId, CameraMode>();
  private activeId: CameraModeId | null = null;
  private lastAspect = 1;

  register(mode: CameraMode): void {
    this.modes.set(mode.id, mode);
    mode.handleResize(this.lastAspect);
  }

  setActive(id: CameraModeId): void {
    const next = this.modes.get(id);
    if (!next) {
      throw new Error(`CameraManager: no camera mode registered for "${id}"`);
    }
    if (this.activeId === id) return;

    const previous = this.activeId ? this.modes.get(this.activeId) : undefined;
    previous?.deactivate();
    next.activate();
    this.activeId = id;
  }

  get activeModeId(): CameraModeId | null {
    return this.activeId;
  }

  get activeCamera(): THREE.Camera {
    if (!this.activeId) {
      throw new Error(
        'CameraManager: no active camera mode set. Call setActive() before rendering.',
      );
    }
    return this.modes.get(this.activeId)!.camera;
  }

  update(deltaSeconds: number): void {
    if (!this.activeId) return;
    this.modes.get(this.activeId)!.update(deltaSeconds);
  }

  /** All registered modes get the new aspect, not just the active one, so switching is never stale. */
  handleResize(aspect: number): void {
    this.lastAspect = aspect;
    for (const mode of this.modes.values()) mode.handleResize(aspect);
  }

  dispose(): void {
    for (const mode of this.modes.values()) mode.dispose();
    this.modes.clear();
    this.activeId = null;
  }
}
